import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, DOCUMENT } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { FontSizeToPx, FontSize, getLocaleForLang, defaultLanguages } from '@constants';
import { LocalStorageService } from '@core/services/local-storage.service';
import { Language } from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import { CommonsFacade } from '@state/commons';
import { UserAuthFacade } from '@state/user-auth';
import { Moment } from 'moment';
import { ReplaySubject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { AnalyticsService } from '@core/services/analytics.service';

@Component({
  selector: 'app-utils-buttons',
  templateUrl: './utils-buttons.component.html',
  styleUrls: ['./utils-buttons.component.scss'],
  standalone: false,
})
export class UtilsButtonsComponent implements OnInit, OnDestroy {
  private readonly ls = inject(LocalStorageService);
  private readonly translateService = inject(TranslateService);
  private readonly userAuthFacade = inject(UserAuthFacade);
  private readonly commonsFacade = inject(CommonsFacade);
  private readonly dateAdapter = inject<DateAdapter<Moment>>(DateAdapter);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly document = inject(DOCUMENT);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  @Input() theme: 'LIGHT' | 'DARK' = 'DARK';

  @Output() userLangChanged: EventEmitter<Language> = new EventEmitter();

  public currentUserLang: Language;
  public availableLanguages: Language[] = [];

  ngOnInit(): void {
    this.commonsFacade.getLanguagesError$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      // if availableLanguages && currentUserLang set then we've loaded langs atleast once
      if (this.availableLanguages?.length && this.currentUserLang) {
        return;
      }
      // if not - use defaults
      this.availableLanguages = [...defaultLanguages];
      this.setupLang();
    });

    this.commonsFacade
      .getLanguages$()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(langs => {
        this.availableLanguages = langs;
        this.setupLang();
      });

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(({ lang: langKey, translations }) => {
      if (!this.availableLanguages?.length) {
        return;
      }
      const lang = this.availableLanguages.find(({ key }) => key === langKey);

      if (!lang) {
        console.error('User Language changed into one that does not exist in availableLanguages collection');
        return;
      }

      this.currentUserLang = lang;
      this.dateAdapter.setLocale(getLocaleForLang(langKey));
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  private setupLang(): void {
    const userLangInLS = this.ls.getUserLang();
    const defaultLang = this.availableLanguages.find(({ key }) => key === 'en');
    const langToUse = userLangInLS?.key ? userLangInLS : defaultLang;

    this.translateService.use(langToUse.key);
    this.currentUserLang = langToUse;

    this.dateAdapter.setLocale(getLocaleForLang(langToUse.key));
  }

  public changeFontSize(): void {
    const currFontSizeVal = this.document.documentElement.style.getPropertyValue('--font-factor') || '0px';
    let nextFontSizeVal;

    if (currFontSizeVal === FontSizeToPx[FontSize.SMALL]) {
      nextFontSizeVal = FontSizeToPx[FontSize.MEDIUM];
    }
    if (currFontSizeVal === FontSizeToPx[FontSize.MEDIUM] || !currFontSizeVal || currFontSizeVal === 'undefined') {
      nextFontSizeVal = FontSizeToPx[FontSize.LARGE];
    }
    if (currFontSizeVal === FontSizeToPx[FontSize.LARGE]) {
      nextFontSizeVal = FontSizeToPx[FontSize.SMALL];
    }

    this.document.documentElement.style.setProperty('--font-factor', nextFontSizeVal);
  }

  public changeLangTo(lang: Language): void {
    this.userLangChanged.emit(lang);
    this.userAuthFacade.myself$.pipe(take(1), takeUntil(this.destroy$)).subscribe(myself => {
      this.translateService.use(lang.key);
      this.ls.setUserLang(lang);
      if (!myself?.role?.key) {
        return;
      }

      const currLangId = lang.id;
      if (!currLangId) {
        return;
      }

      this.analyticsService.trackEvent('user_event', 'user_changed_language', {
        from: myself.languageId,
        to: lang.id,
      });

      this.userAuthFacade.updateMyLanguage({ languageId: currLangId });
    });
  }
}
